import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { SubscriptionGate } from '@/components/paywall/SubscriptionGate';
import { learnerApiJson } from '@/lib/learner-api';
import { stickerSceneGateCopy } from '@/lib/paywall';
import { useLearnerAuth } from '@/providers/LearnerAuth';
import { usePremiumAccess } from '@/hooks/usePremiumAccess';
import { track } from '@/lib/analytics';
import { typeScale } from '@/lib/typography';

type Sticker = { id: number; name: string; image_url: string };
type Pack = { id: number; name: string; stickers: Sticker[] };
type Placement = { id: string; sticker_id: number; x: number; y: number; scale: number; rotation: number; z_index: number };
type Scene = { id: string; title: string; canvas_width: number; canvas_height: number; placements: Placement[]; updated_at: string };

export const Route = createFileRoute('/app/stickers/scenes/$sceneId')({ component: SceneEditor });

function SceneEditor() {
  const { sceneId } = Route.useParams();
  const navigate = useNavigate();
  const auth = useLearnerAuth();
  const premium = usePremiumAccess();
  const [title, setTitle] = useState('My Hmong scene');
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ id: string; moved: boolean } | null>(null);
  const packs = useQuery({ queryKey: ['sticker-packs'], queryFn: () => learnerApiJson<Pack[]>(auth?.getToken ?? null, '/v1/sticker-packs') });
  const scene = useQuery({
    queryKey: ['sticker-scene', sceneId],
    queryFn: async () => {
      const value = await learnerApiJson<Scene>(auth?.getToken ?? null, `/v1/user/sticker-scenes/${sceneId}`, { auth: true });
      setTitle(value.title);
      setPlacements(value.placements);
      return value;
    },
    enabled: sceneId !== 'new' && Boolean(auth?.isSignedIn),
  });
  if (premium.isReady && !premium.isSubscribed) {
    const gate = stickerSceneGateCopy();
    return (
      <SubscriptionGate
        source={gate.source}
        content={sceneId}
        message={gate.message}
        onDismiss={() => void navigate({ to: '/app/stickers' })}
      />
    );
  }
  if (!auth?.isSignedIn) {
    return (
      <main className="grid min-h-[75vh] place-items-center p-6 text-center">
        <div>
          <h1 className={typeScale.screenTitle}>Sign in to save scenes</h1>
          <Link to="/auth/sign-in" className="mt-6 inline-block rounded-2xl bg-[#399918] px-6 py-3 font-bold text-white">Sign in</Link>
        </div>
      </main>
    );
  }
  const stickers = packs.data?.flatMap((pack) => pack.stickers) ?? [];
  const add = (sticker: Sticker) =>
    setPlacements((values) => {
      const next = {
        id: crypto.randomUUID(),
        sticker_id: sticker.id,
        x: 42 + (values.length % 4) * 12,
        y: 42 + (values.length % 5) * 8,
        scale: 1,
        rotation: 0,
        z_index: values.length,
      };
      setSelectedId(next.id);
      return [...values, next];
    });
  const moveToPointer = (id: string, clientX: number, clientY: number) => {
    const box = canvasRef.current?.getBoundingClientRect();
    if (!box) return;
    const x = Math.min(94, Math.max(6, ((clientX - box.left) / box.width) * 100));
    const y = Math.min(94, Math.max(6, ((clientY - box.top) / box.height) * 100));
    setPlacements((values) => values.map((item) => (item.id === id ? { ...item, x, y } : item)));
  };
  const save = async () => {
    setSaving(true);
    try {
      if (sceneId === 'new') {
        const created = await learnerApiJson<Scene>(auth.getToken, '/v1/user/sticker-scenes', {
          method: 'POST',
          auth: true,
          body: JSON.stringify({
            title,
            surface_type: 'journal',
            background_variant: null,
            canvas_width: 1000,
            canvas_height: 1250,
            placements,
          }),
        });
        track('Sticker Scene Created', { placements: placements.length });
        await navigate({ to: '/app/stickers/scenes/$sceneId', params: { sceneId: created.id } });
      } else {
        await learnerApiJson(auth.getToken, `/v1/user/sticker-scenes/${sceneId}`, {
          method: 'PUT',
          auth: true,
          body: JSON.stringify({ title, placements, expected_updated_at: scene.data?.updated_at }),
        });
        track('Sticker Scene Saved', { placements: placements.length });
      }
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mx-auto max-w-6xl px-5 py-6">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/app/stickers" className="font-bold text-[#2d7a13]">← Stickers</Link>
        <input value={title} onChange={(event) => setTitle(event.target.value)} className="min-w-0 flex-1 rounded-xl border bg-white px-4 py-2 font-bold" />
        {selectedId ? (
          <button
            type="button"
            onClick={() => {
              setPlacements((values) => values.filter((item) => item.id !== selectedId));
              setSelectedId(null);
            }}
            className="rounded-xl border border-red-200 px-4 py-2 font-bold text-red-700"
          >
            Remove
          </button>
        ) : null}
        <button onClick={() => void save()} disabled={saving} className="rounded-xl bg-[#399918] px-5 py-2 font-bold text-white">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_18rem]">
        <div
          ref={canvasRef}
          data-testid="sticker-canvas"
          className="relative mx-auto aspect-[4/5] w-full max-w-2xl overflow-hidden rounded-3xl border-8 border-white bg-[linear-gradient(#F3FBF0_31px,#cfe8c6_32px)] bg-[length:100%_32px] shadow-xl"
        >
          {placements.map((placement) => {
            const sticker = stickers.find((item) => item.id === placement.sticker_id);
            if (!sticker) return null;
            return (
              <button
                key={placement.id}
                type="button"
                aria-label={sticker.name}
                onPointerDown={(event) => {
                  event.currentTarget.setPointerCapture(event.pointerId);
                  dragRef.current = { id: placement.id, moved: false };
                  setSelectedId(placement.id);
                }}
                onPointerMove={(event) => {
                  if (!dragRef.current || dragRef.current.id !== placement.id) return;
                  if (Math.abs(event.movementX) + Math.abs(event.movementY) > 2) dragRef.current.moved = true;
                  moveToPointer(placement.id, event.clientX, event.clientY);
                }}
                onPointerUp={() => {
                  dragRef.current = null;
                }}
                style={{
                  left: `${placement.x}%`,
                  top: `${placement.y}%`,
                  transform: `translate(-50%,-50%) scale(${placement.scale}) rotate(${placement.rotation}deg)`,
                  zIndex: placement.z_index,
                }}
                className={`absolute h-24 w-24 touch-none ${selectedId === placement.id ? 'ring-4 ring-[#399918]' : ''}`}
              >
                <img src={sticker.image_url} alt="" className="pointer-events-none h-full w-full object-contain" />
              </button>
            );
          })}
        </div>
        <aside className="max-h-[70vh] overflow-auto rounded-3xl border bg-white p-4">
          <h2 className="font-black">Add a sticker</h2>
          <div className="mt-4 grid grid-cols-3 gap-2">
            {stickers.map((sticker) => (
              <button key={sticker.id} onClick={() => add(sticker)} title={sticker.name} className="aspect-square rounded-xl bg-slate-50 p-1 hover:bg-[#ECFFE6]">
                <img src={sticker.image_url} alt={sticker.name} className="h-full w-full object-contain" />
              </button>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
